// Code-review note: the useEffect below omits stable React setters + module
// constants (axios, API) from its dep array — that's the correct pattern.
// See /app/REVIEW_WAIVERS.md §1 & §3 before flagging exhaustive-deps or
// complexity here.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Quote } from 'lucide-react';
import { testimonials, business } from '../../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Normalises a Google Places review from /api/reviews into the same shape
 * the static `testimonials` array uses so the JSX below stays simple.
 */
function normaliseLive(rv, i) {
  return {
    id: `google-${i}`,
    name: rv.author,
    rating: rv.rating || 5,
    text: rv.text,
    datePublished: rv.published_at,
    dateDisplay: rv.when,
    isLive: true,
  };
}

function ReviewCard({ t, compact = false }) {
  return (
    <article
      className={`gold-top-card flex flex-col ${compact ? 'p-6 w-[360px] shrink-0 h-full' : 'p-7'}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-0.5" aria-label={`${t.rating} out of 5 stars`}>
          {[...Array(t.rating || 5)].map((_, j) => (
            <Star key={`${t.id}-star-${j}`} className="w-4 h-4 fill-[#D98A00] text-[#D98A00]" />
          ))}
        </div>
        <Quote className="w-6 h-6 text-[#0A2E18]/20" aria-hidden="true" />
      </div>
      <p
        className={`text-[#1E281C] leading-relaxed text-[15px] flex-1 mb-5 ${
          compact ? 'line-clamp-5' : ''
        }`}
      >
        &ldquo;{t.text}&rdquo;
      </p>
      <div className="pt-4 border-t border-[#E2DAC5]">
        <div className="font-bold text-[#0A2E18]">{t.name}</div>
        <div className="text-xs text-[#4A5447] mt-0.5 flex items-center gap-1.5 flex-wrap">
          {t.datePublished && (
            <>
              <time dateTime={t.datePublished}>{t.dateDisplay || t.datePublished}</time>
              <span aria-hidden="true">·</span>
            </>
          )}
          <span>{t.isLive ? 'Google Review' : 'Verified Google Review'}</span>
        </div>
      </div>
    </article>
  );
}

function MarqueeRow({ items, direction = 'left', rowKey }) {
  // Duplicate items for seamless infinite scroll (translateX -50% loops cleanly).
  const doubled = [...items, ...items];
  const animClass = direction === 'left' ? 'animate-marquee-left' : 'animate-marquee-right';
  return (
    <div
      className="group/marquee relative overflow-hidden py-2"
      data-testid={`marquee-row-${rowKey}`}
    >
      <div className={`flex gap-5 w-max ${animClass} group-hover/marquee:[animation-play-state:paused]`}>
        {doubled.map((t, i) => (
          <ReviewCard key={`${rowKey}-${t.id}-${i}`} t={t} compact />
        ))}
      </div>
      {/* Edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#F6F0E4] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#F6F0E4] to-transparent" />
    </div>
  );
}

export default function Testimonials({ location, variant = 'grid' }) {
  const heading = location
    ? `Trusted by homeowners in ${location}.`
    : 'Trusted by homeowners across Ballarat.';

  const [items, setItems] = useState(testimonials);
  const [rating, setRating] = useState(business.googleRating);
  const [count, setCount] = useState(business.googleReviewCount);
  const [mapsUrl, setMapsUrl] = useState(business.googleUrl);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reviews`)
      .then((res) => {
        if (cancelled) return;
        const live = res.data?.reviews || [];
        if (live.length === 0) return;
        setItems(live.map(normaliseLive));
        if (res.data?.rating) setRating(res.data.rating);
        if (res.data?.total_reviews) setCount(res.data.total_reviews);
        if (res.data?.google_maps_url) setMapsUrl(res.data.google_maps_url);
      })
      .catch(() => {
        /* keep static fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Split reviews across two rows for the marquee variant.
  const mid = Math.ceil(items.length / 2);
  const topRow = items.slice(0, mid);
  const bottomRow = items.slice(mid);

  return (
    <section id="testimonials" className="py-16 md:py-20 cream-bg">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10 reveal">
          <div className="lg:col-span-8">
            <p className="section-label mb-4">Google Reviews</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              {heading}
            </h2>
          </div>

          <div className="lg:col-span-4 flex items-center gap-2 lg:justify-end">
            <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
              {['s1', 's2', 's3', 's4', 's5'].map((k) => (
                <Star key={`hdr-star-${k}`} className="w-5 h-5 fill-[#D98A00] text-[#D98A00]" />
              ))}
            </div>
            <span className="font-extrabold text-[#0A2E18] text-lg">{Number(rating).toFixed(1)}</span>
            <span className="text-[#212B20] text-sm">based on {count} Google reviews</span>
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-link"
                className="text-sm font-bold text-[#0A2E18] underline decoration-[#D98A00] decoration-2 underline-offset-4 hover:decoration-[#B86F00] ml-2"
              >
                View on Google →
              </a>
            )}
          </div>
        </div>
      </div>

      {variant === 'marquee' ? (
        <div className="space-y-5" data-testid="reviews-marquee">
          <MarqueeRow items={topRow} direction="left" rowKey="top" />
          {bottomRow.length > 0 && (
            <MarqueeRow items={bottomRow} direction="right" rowKey="bottom" />
          )}
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((t) => (
              <ReviewCard key={t.id} t={t} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
